import { useState, useCallback, useEffect, useRef } from "react";
import { useCookies } from "react-cookie";
import useWebSocket from "react-use-websocket";
import { v4 as uuidv4 } from "uuid";

const socketUrl = 'wss://brmm0q8jaa.execute-api.eu-central-1.amazonaws.com/production';

const useZvtTerminal = ({ transactionId }) => {
  const [cookies] = useCookies(["secret", "station", "staff"]);
  const [paymentStatus, setPaymentStatus] = useState({ code: null, amount: null, msg: null });
  const txIdRef = useRef(null);
  const cancelRef = useRef(false);

  const { sendMessage, lastMessage } = useWebSocket(socketUrl, {
    queryParams: {
      station: cookies.station,
      secret: cookies.secret,
      staff: cookies.staff,
    },
    shouldReconnect: () => true,
    retryOnError: true,
  });

  const initiatePayment = useCallback((amount) => {
    if (paymentStatus.code !== null) return;
    const txId = uuidv4();
    txIdRef.current = txId;
    cancelRef.current = false;
    setPaymentStatus({ code: "PENDING", amount, msg: null });

    sendMessage(
      JSON.stringify({
        task: "zvtPayment",
        station: cookies.station,
        staff: cookies.staff,
        amount,
        txId,
        transactionId
      })
    );
  }, [cookies.staff, cookies.station, sendMessage, paymentStatus.code, transactionId]);

  const cancelPayment = useCallback(() => {
    if (paymentStatus.code === "PENDING") {
      cancelRef.current = true;
      setPaymentStatus(prev => ({ ...prev, code: "CANCELING", msg: "Abbruch läuft..." }));
      // optional: sende cancel über WebSocket falls unterstützt
    }
  }, [paymentStatus.code]);

  const clearPayment = useCallback(() => {
    txIdRef.current = null;
    cancelRef.current = false;
    setPaymentStatus({ code: null, amount: null, msg: null });
  }, []);

  useEffect(() => {
    if (!lastMessage) return;

    try {
      const msg = JSON.parse(lastMessage.data);

      if (msg.task === "zvtResponse" && msg.txId === txIdRef.current) {
        const status = msg.data?.status || "";
        const isSuccess = status === "State:Successful";
        const isAbort = status.includes("State:Abort");

        if (isSuccess) {
          setPaymentStatus({ code: "SUCCESS", amount: msg.data.amount, msg: null });
        } else if (isAbort) {
          setPaymentStatus({ code: "ERROR", amount: null, msg: "Karten-Zahlung abgebrochen. Erneut versuchen oder andere Zahlungsmethode verwenden." });
        } else {
          setPaymentStatus({ code: "ERROR", amount: null, msg: "Fehler bei Zahlung" });
        }
      }

      // Benutzerabbruch: Reset bei passender txId
      if (
        msg.task === "zvtResponse" &&
        msg.txId === txIdRef.current &&
        cancelRef.current
      ) {
        setPaymentStatus({ code: null, amount: null, msg: null });
        cancelRef.current = false;
      }

    } catch (err) {
      console.error("WebSocket parse error", err);
    }
  }, [lastMessage]);

  return {
    initiatePayment,
    cancelPayment,
    clearPayment,
    paymentStatus,
  };
};

export { useZvtTerminal };
